import { EventEmitter } from '@angular/core';

import { DragAndDropService, DragAction, DragAndDropEvent } from '../drag-and-drop.service';

export class Draggable {
  constructor(
    private _service: DragAndDropService,
    public readonly id: number,
    public readonly element: HTMLElement
  ) {
    this._service.addEventListener(this.element, 'dragstart', this._onDragStart);
    this._service.addEventListener(this.element, 'drag', this._onDrag, true);
    this._service.addEventListener(this.element, 'dragend', this._onDragEnd);
    this._service.addEventListener(this.element, 'drop', this._onDragEnd);
  }

  public enabled = true;
  public dragging = false;

  public name = '';
  public action: DragAction = 'move';
  public use_placeholder = true;
  public placeholder_class: string = null;
  public data: any = null;

  public onStart: EventEmitter<DragAndDropEvent> = new EventEmitter();
  public onEnd: EventEmitter<DragAndDropEvent> = new EventEmitter();

  public $onStart: EventEmitter<DragEvent> = new EventEmitter();
  public $onEnd: EventEmitter<DragEvent> = new EventEmitter();

  private _drag_placeholder: HTMLElement = document.createElement('div');
  private _drag_start_event: DragEvent;

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  public setDragging(dragging: boolean): void {
    this.dragging = dragging;
  }

  public setName(name: string): void {
    this.name = name;
  }

  public setAction(action: DragAction): void {
    this.action = action;
  }

  public setData(data: any): void {
    this.data = data;
  }

  public setUsePlaceholder(use_placeholder: boolean): void {
    this.use_placeholder = use_placeholder;
  }

  public setPlaceholderClass(placeholder_class: string): void {
    this.placeholder_class = placeholder_class;
  }

  public destroy() {
    this._service.removeEventListener(this.element, 'dragstart', this._onDragStart);
    this._service.removeEventListener(this.element, 'drag', this._onDrag);
    this._service.removeEventListener(document, 'dragend', this._onDragEnd);
    this._service.removeEventListener(this.element, 'drop', this._onDragEnd);
  }

  private _onDragStart = (event: DragEvent): void => {
    event.stopPropagation();

    if (!this.enabled || this.dragging) { return; }

    event.dataTransfer.effectAllowed = this.action;
    event.dataTransfer.dropEffect = this.action;

    const element = this.element;
    const parent = this.element.parentNode;
    const rects = element.getBoundingClientRect();
    const styles = window.getComputedStyle(element);

    if (this.use_placeholder) {
      this._drag_placeholder.classList.add('uex-drag-placeholder');

      if (this.placeholder_class) {
        this._drag_placeholder.classList.add(this.placeholder_class);
      }

      this._drag_placeholder.style.width = `${rects.width}px`;
      this._drag_placeholder.style.height = `${rects.height}px`;
      this._drag_placeholder.style.borderRadius = styles.borderRadius || null;
      this._drag_placeholder.style.marginTop = styles.marginTop || null;
      this._drag_placeholder.style.marginRight = styles.marginRight || null;
      this._drag_placeholder.style.marginBottom = styles.marginBottom || null;
      this._drag_placeholder.style.marginLeft = styles.marginLeft || null;

      parent.insertBefore(this._drag_placeholder, element.nextSibling);
    }

    if (this.data) {
      event.dataTransfer.setData('text/plain', JSON.stringify({data: this.data }));
    }

    this._drag_start_event = event;
  }

  private _onDrag = (event: DragEvent): void => {
    if (!this.enabled) { return; }

    if (!this.dragging) {
      if (this.use_placeholder) {
        this._drag_placeholder.style.display = 'block';
      }

      this.dragging = true;
      this.$onStart.emit(this._drag_start_event);
    }
  }

  private _onDragEnd = (event: DragEvent): void => {
    if (!this.dragging) { return; }

    this._drag_start_event = null;

    this.dragging = false;

    event.dataTransfer.clearData();

    if (this.use_placeholder) {
      this._drag_placeholder.style.display = 'none';
      if (this._drag_placeholder.parentNode) {
        this._drag_placeholder.parentNode.removeChild(this._drag_placeholder);
      }
    }

    this.$onEnd.emit(event);
  }
}
